import React, { useState, useEffect, useRef } from 'react';
import { format, addDays, startOfWeek } from 'date-fns';
import { MapPin } from 'lucide-react';

// Colors for different days
const dayColors = {
    Monday: '#EF4444',     // red-500
    Tuesday: '#F59E0B',    // amber-500
    Wednesday: '#10B981',  // emerald-500
    Thursday: '#3B82F6',   // blue-500
    Friday: '#8B5CF6',     // violet-500
    Saturday: '#EC4899',   // pink-500
    Sunday: '#6366F1'      // indigo-500
};

const VisitMap = ({ organizedVisits }) => {
    const [selectedDays, setSelectedDays] = useState(() => {
        // Initialize with all days that have valid postcodes
        const daysWithPostcodes = new Set();
        organizedVisits.forEach((visits, date) => {
            if (visits.some(visit => visit.customer_postcode)) {
                daysWithPostcodes.add(date);
            }
        });
        return daysWithPostcodes;
    });
    const [map, setMap] = useState(null);
    const [markers, setMarkers] = useState([]);
    const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
    const mapRef = useRef(null);
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });

    // Check if Google Maps is loaded
    useEffect(() => {
        const checkGoogleMapsLoaded = () => {
            if (window.google && window.google.maps) {
                setIsGoogleMapsLoaded(true);
            } else {
                setTimeout(checkGoogleMapsLoaded, 100);
            }
        };
        checkGoogleMapsLoaded();
    }, []);

    // Initialize map once Google Maps is loaded and ref is available
    useEffect(() => {
        if (isGoogleMapsLoaded && mapRef.current && !map) {
            const newMap = new window.google.maps.Map(mapRef.current, {
                zoom: 7,
                center: { lat: 54.5, lng: -2 }, // Center on UK
                styles: [
                    {
                        featureType: "poi",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }]
                    }
                ]
            });
            setMap(newMap);
        }
    }, [isGoogleMapsLoaded, map]);

    // Update markers when selected days change
    useEffect(() => {
        if (!map) return;

        // Clear existing markers
        markers.forEach(marker => marker.setMap(null));
        setMarkers([]);

        const newMarkers = [];
        let bounds = new window.google.maps.LatLngBounds();
        let hasValidPostcodes = false;

        selectedDays.forEach(date => {
            const dayVisits = organizedVisits.get(date) || [];
            const dayName = format(new Date(date), 'EEEE');

            dayVisits.forEach(visit => {
                if (!visit.customer_postcode) return;

                // Here you would need to geocode the postcode to get lat/lng
                // For demo purposes, using random coordinates around the UK
                const lat = 51.5 + (Math.random() - 0.5) * 5;
                const lng = -0.1 + (Math.random() - 0.5) * 5;

                const marker = new window.google.maps.Marker({
                    position: { lat, lng },
                    map: map,
                    title: `${visit.customer_account} - ${format(new Date(visit.visit_datetime), 'HH:mm')}`,
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: dayColors[dayName],
                        fillOpacity: 0.7,
                        strokeWeight: 2,
                        strokeColor: dayColors[dayName]
                    }
                });

                // Add click listener for marker
                marker.addListener('click', () => {
                    const infoWindow = new window.google.maps.InfoWindow({
                        content: `
              <div class="p-2">
                <div class="font-bold">${visit.customer_account}</div>
                <div>${visit.customer_postcode}</div>
                <div>${format(new Date(visit.visit_datetime), 'HH:mm')} - ${dayName}</div>
              </div>
            `
                    });
                    infoWindow.open(map, marker);
                });

                bounds.extend(marker.getPosition());
                newMarkers.push(marker);
                hasValidPostcodes = true;
            });
        });

        setMarkers(newMarkers);

        // Fit bounds if we have markers
        if (hasValidPostcodes && newMarkers.length > 0) {
            map.fitBounds(bounds);
        }
    }, [map, selectedDays, organizedVisits]);

    const toggleDay = (date) => {
        const newSelected = new Set(selectedDays);
        if (newSelected.has(date)) {
            newSelected.delete(date);
        } else {
            newSelected.add(date);
        }
        setSelectedDays(newSelected);
    };

    return (
        <div className="bg-white rounded-lg shadow mt-6">
            <div className="p-4 border-b flex items-center gap-2">
                <MapPin className="h-5 w-5 text-gray-400" />
                <h2 className="font-medium">Visit Locations</h2>
            </div>

            {/* Day toggles */}
            <div className="p-4 flex flex-wrap gap-2">
                {Array.from(organizedVisits.keys()).map(date => {
                    const dayName = format(new Date(date), 'EEEE');
                    const hasPostcodes = (organizedVisits.get(date) || []).some(visit => visit.customer_postcode);

                    return (
                        <button
                            key={date}
                            onClick={() => toggleDay(date)}
                            className={`px-4 py-2 rounded-lg border transition-colors ${selectedDays.has(date)
                                ? 'border-gray-300 bg-gray-50'
                                : 'border-gray-200 bg-white'
                                } ${hasPostcodes
                                    ? 'hover:bg-gray-100'
                                    : 'opacity-50 cursor-not-allowed'
                                }`}
                            disabled={!hasPostcodes}
                            style={{
                                borderLeftWidth: '4px',
                                borderLeftColor: dayColors[dayName]
                            }}
                        >
                            <span className="font-medium">{dayName}</span>
                            <span className="text-sm text-gray-500 ml-2">
                                ({(organizedVisits.get(date) || []).filter(v => v.customer_postcode).length})
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Map container */}
            <div
                ref={mapRef}
                className="w-full h-[42rem] rounded-b-lg"
            />

            {!isGoogleMapsLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 rounded-lg">
                    <div className="text-gray-500">Loading map...</div>
                </div>
            )}
        </div>
    );
};

export default VisitMap;











